import React, { useEffect, useState, useRef, useMemo } from 'react';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { useSearchParams, createSearchParams, useNavigate } from 'react-router-dom';
import { prefictureList } from '@/features/properties/hoc/SearchConst';
import { getNameFromArrayById, setQueryParam } from '@/utility/Helper';
import { SearchAtoms } from '@/features/properties/hoc/SearchAtom';
import { useForm } from 'react-hook-form';
import { ButtonSubmit } from '@/components/ButtonSubmit';
import { useAtomValue, useSetAtom } from 'jotai';
import { SearchCommon } from '@/features/properties/components/SearchCommon';
import IconStorefront from '@/assets/icon_storefront.svg';
import { useGetMStationList } from '@/apis/core/mStation';
import { GetMOptionList200ResponseBody } from 'openapi/omise-craft-core-spec';
import { useGetMOptionList } from '@/apis/core/mOption';
import { ChkboxProperty } from '../SearchCommon/ChkboxProperty';
import { SignUpFab } from '@/components/SignUpFab';
import { AuthAtoms } from '@/features/login/hoc/AuthAtom';
import { MemberNewsModal } from '@/components/memberNewsModal';

/**
 * 物件検索_駅選択
 *
 * @returns
 */

type prefecturesprops = {
  searchPath: string;
};

interface StationInfo {
  id: number | undefined;
  name: string;
  property_count: number;
}

interface LineInfo {
  id: number | undefined;
  name: string;
  stations: Array<StationInfo>;
}

export const Stations = (props: prefecturesprops) => {
  const [searchParams] = useSearchParams();
  const prefecturePrm = searchParams.get('prefectures');
  const setPref = useSetAtom(SearchAtoms.prefectures);
  let prefectureNum: number | undefined = undefined;
  let prefecture = '';
  if (prefecturePrm) {
    prefectureNum = Number(prefecturePrm);
    prefecture = prefecturePrm;
  }
  setPref(prefectureNum);
  const SearchAtomsValue = {
    prefectures: useAtomValue(SearchAtoms.prefectures),
    line: useAtomValue(SearchAtoms.line),
    station: useAtomValue(SearchAtoms.station),
    distanceMinutesMin: useAtomValue(SearchAtoms.distanceMinutesMin),
    distanceMinutesMax: useAtomValue(SearchAtoms.distanceMinutesMax),
    priceMin: useAtomValue(SearchAtoms.priceMin),
    priceMax: useAtomValue(SearchAtoms.priceMax),
    tsuboMin: useAtomValue(SearchAtoms.tsuboMin),
    tsuboMax: useAtomValue(SearchAtoms.tsuboMax),
    age: useAtomValue(SearchAtoms.age),
    style: useAtomValue(SearchAtoms.possibleUsage),
    status: useAtomValue(SearchAtoms.status),
    floorNumber: useAtomValue(SearchAtoms.floorNumber),
    hasExperienceRestaurant: useAtomValue(SearchAtoms.hasExperienceRestaurant),
    possibleUsage: useAtomValue(SearchAtoms.possibleUsage),
    plannedOpeningArea: useAtomValue(SearchAtoms.plannedOpeningArea),
    isIncludeHidden: useAtomValue(SearchAtoms.isIncludeHidden),
    isIncludeDeleted: useAtomValue(SearchAtoms.isIncludeDeleted),
    type: 'station'
  };

  // 選択された路線ID（複数）
  const [mLines, setMLines] = useState<LineInfo[]>([]);
  const lines = SearchAtomsValue.line.filter((obj) => obj.value).map((obj) => Number(obj.value));

  const [isVisible, setIsVisible] = useState(false);
  const targetRef = useRef(null);

  // 選択肢マスタ
  const [mOption, setMOption] = useState<GetMOptionList200ResponseBody | undefined>(undefined);
  const mOptionList = useGetMOptionList();

  // 選択肢マスタ取得
  useEffect(() => {
    if (mOptionList.isSuccess) {
      setMOption(mOptionList.data);
    }
    console.warn(mOption);
  }, [mOptionList.isSuccess]);

  const stationsResponse = useGetMStationList(undefined, lines, undefined, 1);

  useEffect(() => {
    if (lines.length === 0) {
      const prefectureParam = { prefectures: prefecture };

      navigate({
        pathname: '/properties/route',
        search: `?${createSearchParams(prefectureParam)}`
      });
    }

    // 追従エリア表示・非表示
    if (targetRef.current) {
      const observer = new IntersectionObserver(([entry]) => {
        setIsVisible(entry.isIntersecting);
      });
      observer.observe(targetRef.current);
    }
  }, []);

  useEffect(() => {
    const mStationsData = stationsResponse?.data?.stations && stationsResponse.data.stations;
    const mLineInfo: LineInfo[] = [];
    if (mStationsData !== undefined) {
      for (let i = 0; i < mStationsData.length; i++) {
        let exists = false;
        for (let j = 0; j < mLineInfo.length; j++) {
          if (mLineInfo[j].id === mStationsData[i].line_id) {
            mLineInfo[j].stations.push({
              id: mStationsData[i].id,
              name: mStationsData[i].name,
              property_count: mStationsData[i].property_count
            });
            exists = true;
            break;
          }
        }

        if (!exists) {
          const station = {
            id: mStationsData[i].id,
            name: mStationsData[i].name,
            property_count: mStationsData[i].property_count
          };
          const stations = [];
          stations.push(station);
          mLineInfo.push({
            id: mStationsData[i].line_id,
            name: mStationsData[i].line_name,
            stations: stations
          });
        }
      }
    }
    setMLines(mLineInfo);
  }, [stationsResponse.data, stationsResponse.isSuccess]);

  const navigate = useNavigate();

  const prefectureName = getNameFromArrayById(prefictureList, prefectureNum);
  const {
    control,
    setValue,
    handleSubmit,
    formState: { errors }
  } = useForm();

  const isAuth = useAtomValue(AuthAtoms.isAuth);

  // 検証用ダミー
  const breadcrumbList = [
    {
      value: 'エリア選択',
      link: true,
      path: '/properties/prefectures'
    },
    {
      value: prefectureName,
      link: false,
      path: ''
    },
    {
      value: prefectureName + 'の路線選択',
      link: true,
      path: '/properties/route?prefectures=' + prefecture
    },
    {
      value: prefectureName + 'の駅',
      link: false,
      path: ''
    }
  ];

  const onSubmitEvent = () => {
    const searchQueryParam = setQueryParam(SearchAtomsValue);

    navigate({
      pathname: props.searchPath,
      search: `?${createSearchParams(searchQueryParam)}`
    });
  };

  const setChecked = useSetAtom(SearchAtoms.station);

  useEffect(() => {
    setChecked([]);
  }, [setChecked]);

  // お知らせモーダル表示
  const setNewsModal = useMemo(() => {
    return <MemberNewsModal isOpen={true} />;
  }, []);

  return (
    <div>
      {setNewsModal}
      <Breadcrumbs breadcrumbs={breadcrumbList} />

      <div className="bg-contents md:mx-auto mx-0 md:max-w-[1230px] md:p-[30px] p-[20px] md:pb-[40px] rounded-[15px]">
        <h1 className="text-sub text-2xl">
          <span>
            <img className="inline w-[30px] h-[30px] mr-[20px]" src={IconStorefront} />
          </span>
          <span>物件検索</span>
        </h1>

        <h2 className="text-sub text-left md:mt-[40px] mt-[30px]">駅名から探す</h2>

        <form>
          <div>
            {lines.map((line) => {
              const lineData = mLines.find((v) => v.id == Number(line));
              if (lineData) {
                return (
                  <div className="mx-auto md:mt-[30px] mt-[20px] max-h-fit h-fit w-full" key={line}>
                    <ChkboxProperty
                      areaClass=""
                      label={lineData.name}
                      labelClass="md:mt-[15px] mt-[20px]"
                      list={lineData.stations}
                      control={control}
                      required
                      inputAreaClass=""
                      inputAreaWidth=" !bg-background md:w-[370px] w-full md:min-h-[40px] !m-0 pr-[20px] text-left "
                      name="municipalities"
                      listClass="stationArea flex justify-start items-wrap gap-[20px_30px]"
                      atom={SearchAtoms.station}
                      listLabelClass="text-left"
                      selectAllLabel={'すべての駅を選択'}
                      setValue={setValue}
                    />
                  </div>
                );
              }
            })}
          </div>
          {Object.keys(errors).length > 0 && (
            <p className="mt-[10px] text-error font-bold text-xs text-left">{'駅は、最低1つを選択してください'}</p>
          )}
          <div className="md:mt-[60px] md:my-0 my-[30px]">
            <ButtonSubmit
              onclickEvent={handleSubmit(onSubmitEvent)}
              label="選択した駅名で物件検索する"
              buttonClass=" searchButton "
              id="searchFirst"
              disabled={Object.keys(errors).length > 0}
            />
          </div>
          <div className="mx-auto md:mt-[40px] mt-[30px] md:p-[30px] p-[20px] bg-background rounded-[15px]">
            <SearchCommon control={control} mOption={mOption} />
          </div>
          <div className="mt-[30px]" ref={targetRef}>
            <ButtonSubmit
              onclickEvent={handleSubmit(onSubmitEvent)}
              label="物件検索する"
              buttonClass=" searchButton "
              id="searchSecond"
              disabled={Object.keys(errors).length > 0}
            />
          </div>
        </form>
      </div>
      {/* 追従エリア */}
      {isVisible ? (
        ''
      ) : (
        <div className="visibleArea sticky w-full h-[84px] flex justify-center items-center bg-[#00000080] bottom-0 left-0">
          <ButtonSubmit
            onclickEvent={handleSubmit(onSubmitEvent)}
            label="物件検索する"
            buttonClass=" searchButton hover:!text-contents md:!w-[370px] !w-[calc(100%_-_80px)] !m-0"
            disabled={Object.keys(errors).length > 0}
          />
        </div>
      )}
      {!isAuth && <SignUpFab />}
    </div>
  );
};
